import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  Switch,
  Tooltip,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core';
import { MoreVert as MoreVertIcon } from '@material-ui/icons';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ManagerService from '../../../services/ManagerService';
// utils

const useStyles = makeStyles((theme) => ({
  root: {},
  moreVert: {
    cursor: 'pointer',
    color: 'inherit'
  },
  edit: {
    cursor: 'pointer'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  dateAfter: {
    color: '#bbb'
  },
  true: {
    fill: theme?.palette?.primary?.main,
  },
  false: {
    fill: '#aaa'
  },
  statusCell: {
    position: 'relative'
  },
  statusPanel: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 120,
    backgroundColor: 'white',
    borderRadius: '.2rem',
    border: '1px solid #ddd',
  },
  statusPanelList: {
    listStyle: 'none'
  },
  statusPanelItem: {
    padding: 0,
    margin: 0,
    '& > a': {
      display: 'block',
      padding: '.75rem 1rem',
      cursor: 'pointer',
      fontFamily: 'sans-serif',
      fontSize: '.9rem',
      '&:hover': {
        backgroundColor: '#eee'
      }
    }
  }
}));

const Results = ({ className, data, ...rest }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [tooltipOpen, setTooltipOpen] = useState('');
  const [tooltipTitle, setTooltipTitle] = useState('');
  const [limit, setLimit] = useState(100);
  const [page, setPage] = useState(0);
  const [managers, setManagers] = useState([]);
  const [panel, setPanel] = useState(null);
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 });

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const toggleIsOrderTakerManager = async (manager) => {
    const response = await ManagerService.toggleIsOrderTaker(manager?.id);
    setTooltipOpen('');
    setTooltipTitle('');

    if (response?.code === 200) {
      const newManagers = managers.reduce((result, item) => {
        if (item?.id === manager?.id) {
          return result.concat({ ...item, is_order_taker: !item?.is_order_taker });
        }
        return result.concat(item);
      }, []);

      setManagers(newManagers);
    }

    if (response?.code === 409) {
      setTooltipTitle(response?.errors[0]);
      setTooltipOpen(manager?.id);

      setTimeout(() => {
        setTooltipOpen('');
        setTooltipTitle('');
      }, 5000);
    }
  };

  const goToEdit = () => {
    navigate(`/app/gerentes/${panel}/editar`, { replace: true });
  };

  const goToEditPassword = () => {
    navigate(`/app/gerentes/${panel}/editar?type=password`, { replace: true });
  };

  const onClick = (e, coupon) => {
    setPanel(panel ? null : coupon?.id);
    setPanelPosition(panel ? { x: 0, y: 0 } : { x: e.clientX, y: e.clientY });
  };

  const closePanel = () => {
    setTimeout(() => {
      setPanel(null);
      setPanelPosition({ x: 0, y: 0 });
    }, 50);
  };

  useEffect(() => {
    if (Array.isArray(data)) {
      setManagers(data);
    }
  }, [data]);

  useEffect(() => {
    if (panel) {
      document.addEventListener('click', closePanel);
    }

    return () => {
      document.removeEventListener('click', closePanel);
    };
  }, [panel]);

  return (
    <>
      <Card
        className={clsx(classes.root, className)}
        {...rest}
      >
        <PerfectScrollbar>
          <Box minWidth={1050}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    Nombre
                  </TableCell>
                  <TableCell>
                    Teléfono
                  </TableCell>
                  <TableCell>
                    Correo
                  </TableCell>
                  <TableCell>
                    Sucursal
                  </TableCell>
                  <TableCell>
                    Recibidor de ordenes
                  </TableCell>
                  <TableCell width="5" />
                </TableRow>
              </TableHead>
              <TableBody>
                {managers.slice(page * limit, (limit * (page + 1))).map((manager) => (
                  <TableRow
                    hover
                    key={manager?.id}
                  >
                    <TableCell>
                      <Box
                        alignItems="center"
                        display="flex"
                      >
                        <Avatar
                          className={classes.avatar}
                          src={manager?.avatar}
                        />
                        <Typography
                          color="textPrimary"
                          variant="body2"
                        >
                          {manager?.first_name}
                          <br />
                          {manager?.last_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color="textPrimary"
                        variant="body2"
                      >
                        {manager?.phone || 'Sin registrar'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color="textPrimary"
                        variant="body2"
                      >
                        {manager?.email || 'Sin registrar'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color="textPrimary"
                        variant="body2"
                      >
                        {manager?.branch_office?.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={tooltipTitle} open={tooltipOpen === manager?.id}>
                        <Switch
                          color="primary"
                          checked={manager?.is_order_taker}
                          onChange={() => manager && toggleIsOrderTakerManager(manager)}
                          name="manager_is_order_taker"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes?.statusCell}>
                      <MoreVertIcon
                        className={classes?.moreVert}
                        onClick={(e) => onClick(e, manager)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </PerfectScrollbar>
        <TablePagination
          component="div"
          count={managers.length}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleLimitChange}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[10, 100]}
          labelRowsPerPage="Filas por página"
        />
      </Card>
      {
         panel !== null && (
         <div
           className={classes?.statusPanel}
           style={{
             top: panelPosition?.y,
             left: panelPosition?.x - 120
           }}
         >
           <ul className={classes?.statusPanelList}>
             <li className={classes?.statusPanelItem}>
               <a onClick={() => goToEdit()}>
                 Editar
               </a>
             </li>
             <li className={classes?.statusPanelItem}>
               <a onClick={() => goToEditPassword()}>
                 Editar contraseña
               </a>
             </li>
           </ul>
         </div>
         )
        }
    </>
  );
};

Results.propTypes = {
  className: PropTypes.string,
  data: PropTypes.object.isRequired
};

export default Results;
